/**
 * B356 — the icon wall's other half: the ONE place icon geometry lives
 * (DESIGN-V2 §7). Flat 1.5px-stroke vectors on a 16-grid, single tone
 * (currentColor), round caps and joins — never emoji, never font glyphs.
 * The vocabulary wall refuses glyph icons in rendered strings; this
 * component is the cure it points at. New icons are added HERE (with a
 * gallery entry via the section-exists wall), never inlined in an app —
 * the app-drift wall refuses raw <svg> in apps by construction.
 *
 * Decorative by default (`aria-hidden`): an icon beside its own words
 * would say everything twice. Pass `label` when the icon stands alone.
 */
import type { ReactNode } from "react";

/** name → path data on the 16-grid. Stroke-only: fills stay "none". */
const GLYPHS: Readonly<Record<string, readonly string[]>> = {
  /** leaves the app / opens away — the ↗ replacement (B282's hint). */
  "arrow-out": ["M5 11 11 5", "M6.2 5H11v4.8"],
  calendar: ["M3 4.5h10V13H3z", "M3 7.5h10", "M5.5 2.5v2.5", "M10.5 2.5v2.5"],
  /** the browse-along cart badge — the 🛒 replacement (B281). */
  cart: ["M2 3h1.7l1.5 6.8h6.3l1.5-5H4.4", "M6.4 12.6h.01", "M10.6 12.6h.01"],
  check: ["M3.5 8.5l3 3 6-6.5"],
  "chevron-down": ["M4.5 6.5 8 10l3.5-3.5"],
  close: ["M4.5 4.5l7 7", "M11.5 4.5l-7 7"],
  copy: ["M6 6h6.5v6.5H6z", "M3.5 10V3.5H10"],
  lock: ["M4 7.5h8V13H4z", "M5.75 7.5V5.6a2.25 2.25 0 0 1 4.5 0v1.9"],
  /** B645 — "this can be changed": the swatch chip's picker cue. */
  pencil: ["M3.5 12.5l.6-2.6 6.6-6.6a1.2 1.2 0 0 1 1.7 0l.3.3a1.2 1.2 0 0 1 0 1.7l-6.6 6.6z", "M9.8 4.2l2 2"],
  /** B890 — the composer's + context door (723:157). */
  plus: ["M8 3.5v9", "M3.5 8h9"],
  search: ["M7 3.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z", "M12.5 12.5 9.7 9.7"],
  /** the brand's four-point spark — the ✦ replacement (the ask bar). */
  spark: ["M8 2.8c.5 2.6 1.7 3.8 4.3 4.3-2.6.5-3.8 1.7-4.3 4.3-.5-2.6-1.7-3.8-4.3-4.3 2.6-.5 3.8-1.7 4.3-4.3z"],
};

/** The closed, sorted set — the icon test pins it, the gallery shows it. */
export const ICON_NAMES = Object.keys(GLYPHS).sort() as readonly IconName[];

export type IconName =
  | "arrow-out"
  | "calendar"
  | "cart"
  | "check"
  | "chevron-down"
  | "close"
  | "copy"
  | "lock"
  | "pencil"
  | "plus"
  | "search"
  | "spark";

export function Icon(props: { readonly name: IconName; readonly size?: number; readonly label?: string; readonly className?: string }): ReactNode {
  const size = props.size ?? 16;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className ? `t-icon ${props.className}` : "t-icon"}
      aria-hidden={props.label === undefined ? true : undefined}
      role={props.label === undefined ? undefined : "img"}
      aria-label={props.label}
    >
      {props.label === undefined ? null : <title>{props.label}</title>}
      {GLYPHS[props.name]?.map((d) => <path key={d} d={d} />)}
    </svg>
  );
}
